//src/pages/inventory/Pharmacy/Prescriptions.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Button,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { getPrescriptionsByPatientId } from "../../../services/prescriptionService";
import { getPatientById } from "../../../services/patientService";
import { getInvoicesByPatientId, getInvoiceById } from "../../../services/invoiceService";
import { getMedicineTransactionsByPatientId } from "../../../services/medicineTransactionService";
import Header from "../../../components/Header";
import dayjs from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IssueMedicineModal from "./IssueMedicineModal"; // Import the modal
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import Refresh from "@mui/icons-material/Refresh";
import PrintIcon from "@mui/icons-material/Print";
import {
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../../utils/pageLayoutStyles";
import { downloadStyledTableExcel } from "../../../utils/patientTableExcelExport";
import { useAuth } from "../../../context/AuthContext";
import { printInvoiceFromTable } from "../../../utils/invoiceReprintUtils";
import { toPeriodDateKey } from "../../../utils/patientPeriodSummary";
import { toast } from "react-toastify";

// const tabLabels = ['Prescriptions', 'Vital Signs'];
const tabLabels = ['Prescriptions'];

const normalizeMedicineName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getInvoiceRawType = (invoice) =>
  String(invoice?.invoiceType || invoice?.invoice_type || "")
    .trim()
    .toUpperCase();

const isMedicineInvoice = (invoice) => getInvoiceRawType(invoice) === "MEDICINE";

const getTxInvoiceId = (tx) =>
  tx?.invoiceId ?? tx?.invoice_id ?? tx?.invoice?.id ?? null;

const getTxMedicineNames = (tx) => {
  const nested = Array.isArray(tx?.medicines) ? tx.medicines : [];
  if (nested.length) {
    return nested
      .map((med) => normalizeMedicineName(med.medicineName || med.name || med.medicine))
      .filter(Boolean);
  }
  const single = normalizeMedicineName(
    tx?.medicineName || tx?.name || tx?.medicine
  );
  return single ? [single] : [];
};

const pickLatestInvoice = (invoices = []) => {
  if (!Array.isArray(invoices) || !invoices.length) return null;
  return [...invoices].sort((a, b) => {
    const dateA = dayjs(a?.date ?? a?.createdAt ?? a?.created_at);
    const dateB = dayjs(b?.date ?? b?.createdAt ?? b?.created_at);
    if (dateA.isValid() && dateB.isValid()) return dateB.valueOf() - dateA.valueOf();
    return (Number(b?.id) || 0) - (Number(a?.id) || 0);
  })[0];
};

const Prescriptions = ({ patientId: propPatientId, onBack }) => {
  const { patientId: paramPatientId } = useParams();
  const patientId = propPatientId || paramPatientId; // Use prop if provided, otherwise use URL param
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPharmacyAdmin =
    String(user?.roleName || user?.role || "")
      .trim()
      .toLowerCase() === "pharmacy admin";
  const [prescriptions, setPrescriptions] = useState([]);
  const [patientInfo, setPatientInfo] = useState([]);
  const [medicineInvoices, setMedicineInvoices] = useState([]);
  const [medicineTransactions, setMedicineTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [openModal, setOpenModal] = useState(false); // State for modal
  // const [selectedMedicine, setSelectedMedicine] = useState(null); // State for selected medicine
  const [selectedMedicines, setSelectedMedicines] = useState([]); // State for selected medicines
  const [selectedPrescription, setSelectedPrescription] = useState(null); // ✅ State for selected prescription (to access doctorName)
  const [durations, setDurations] = useState({}); // Store durations for each medicine
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDoctor, setFilterDoctor] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [printingInvoiceId, setPrintingInvoiceId] = useState(null);

  useEffect(() => {
    fetchPrescriptions();
    fetchPatientInfo();
    fetchIssuedMedicineData();
  }, [patientId]);

  const fetchPrescriptions = async () => {
    try {
      const data = await getPrescriptionsByPatientId(patientId);
      const sorted = [...(data || [])].sort((a,b)=>
        ((b.id ?? b.prescriptionId ?? 0) - (a.id ?? a.prescriptionId ?? 0)) ||
        (new Date(b.createdAt) - new Date(a.createdAt))
      );
      setPrescriptions(sorted);
    } catch (error) {
      console.error("Failed to fetch prescriptions", error);
    }
  };

  const fetchPatientInfo = async () => {
    try {
      const data = await getPatientById(patientId);
      setPatientInfo(data);
    } catch (error) {
      console.error("Failed to fetch patient info", error);
    }
  };

  const fetchIssuedMedicineData = async () => {
    if (patientId == null || patientId === "" || patientId === "undefined") {
      setMedicineInvoices([]);
      setMedicineTransactions([]);
      return;
    }
    try {
      const [invoices, transactions] = await Promise.all([
        getInvoicesByPatientId(patientId).catch(() => []),
        getMedicineTransactionsByPatientId(patientId).catch(() => []),
      ]);
      setMedicineInvoices(
        (Array.isArray(invoices) ? invoices : []).filter(isMedicineInvoice)
      );
      setMedicineTransactions(Array.isArray(transactions) ? transactions : []);
    } catch (error) {
      console.error("Failed to fetch issued medicine data", error);
      setMedicineInvoices([]);
      setMedicineTransactions([]);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleIssueMedicine = (prescription) => {
  setSelectedMedicines(prescription.medicines); // Set selected medicines from the prescription
  setSelectedPrescription(prescription); // ✅ Store the full prescription to access doctorName
  const initialDurations = prescription.medicines.reduce((acc, medicine) => {
    acc[medicine.name] = medicine.duration || ""; // Initialize with existing duration or empty string
    return acc;
  }, {});
  setDurations(initialDurations); // Set initial durations
  setOpenModal(true);
};

  const handleModalClose = () => {
   setOpenModal(false);
   setSelectedMedicines([]); // Reset to an empty array
   setSelectedPrescription(null); // ✅ Reset selected prescription
};

  // Issue medicine submit form
const handleMedicineSubmit = async (medicineData) => {
  try {
    await Promise.all([fetchPrescriptions(), fetchIssuedMedicineData()]);
    // Optional: Show success message or perform other actions
  } catch (error) {
    console.error("Error handling medicine submission:", error);
  }
};

  // Build filtered + sorted list
  const filteredList = (prescriptions || []).filter((p) => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const vals = [p.id, p.prescriptionId, p.doctorName, p.createdAt, ...(Array.isArray(p.medicines) ? p.medicines.flatMap(m => [m.name, m.notes, m.dosage, m.frequency, m.duration]) : [])]
        .filter(Boolean)
        .map(v => String(v).toLowerCase());
      if (!vals.some(v => v.includes(q))) return false;
    }
    if (filterDoctor.trim()) {
      if (!String(p.doctorName || "").toLowerCase().includes(filterDoctor.toLowerCase())) return false;
    }
    if (fromDate || toDate) {
      const d = p.createdAt ? new Date(p.createdAt) : null;
      if (!d) return false;
      if (fromDate && d < new Date(fromDate)) return false;
      if (toDate) { const toEnd = new Date(toDate); toEnd.setHours(23,59,59,999); if (d > toEnd) return false; }
    }
    return true;
  }).sort((a,b)=> ((b.id ?? b.prescriptionId ?? 0) - (a.id ?? a.prescriptionId ?? 0)) || (new Date(b.createdAt) - new Date(a.createdAt)));

  // Group prescriptions by date
  const groupedPrescriptions = filteredList.reduce((acc, prescription) => {
    const date = dayjs(prescription.createdAt).format("YYYY-MM-DD");
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(prescription);
    return acc;
  }, {});

  const findIssuedMedicineInvoiceForDate = (dateKey, dayPrescriptions = []) => {
    const prescriptionIds = new Set(
      (Array.isArray(dayPrescriptions) ? dayPrescriptions : [])
        .map(
          (prescription) =>
            prescription?.id ??
            prescription?.prescriptionId ??
            prescription?.prescription_id
        )
        .filter((id) => id != null && id !== "")
        .map(String)
    );

    // Prefer invoices that store prescription_id for this visit's prescriptions
    if (prescriptionIds.size) {
      const byPrescription = medicineInvoices.filter((invoice) => {
        const invoicePrescId =
          invoice?.prescriptionId ?? invoice?.prescription_id ?? null;
        return (
          invoicePrescId != null &&
          invoicePrescId !== "" &&
          prescriptionIds.has(String(invoicePrescId))
        );
      });
      if (byPrescription.length) {
        return pickLatestInvoice(byPrescription);
      }
    }

    const prescribedNames = new Set(
      (Array.isArray(dayPrescriptions) ? dayPrescriptions : [])
        .flatMap((prescription) =>
          Array.isArray(prescription?.medicines) ? prescription.medicines : []
        )
        .map((medicine) => normalizeMedicineName(medicine?.name))
        .filter(Boolean)
    );

    const invoicesOnDate = medicineInvoices.filter(
      (invoice) =>
        toPeriodDateKey(invoice?.date ?? invoice?.createdAt ?? invoice?.created_at) ===
        dateKey
    );
    if (invoicesOnDate.length) {
      return pickLatestInvoice(invoicesOnDate);
    }

    const linkedInvoiceIds = new Set();
    (medicineTransactions || []).forEach((tx) => {
      if (String(tx?.transactionType || "").toUpperCase() !== "OUT") return;
      const invoiceId = getTxInvoiceId(tx);
      if (invoiceId == null || invoiceId === "") return;

      const txDateKey = toPeriodDateKey(
        tx?.transactionDate ?? tx?.transaction_date ?? tx?.createdAt
      );
      const names = getTxMedicineNames(tx);
      const nameMatches =
        prescribedNames.size > 0 &&
        names.some((name) => prescribedNames.has(name));

      // Issued on this prescription date, or later for the same prescribed medicines.
      if (txDateKey === dateKey || (nameMatches && txDateKey && txDateKey >= dateKey)) {
        linkedInvoiceIds.add(String(invoiceId));
      }
    });

    if (!linkedInvoiceIds.size) return null;

    const linkedInvoices = medicineInvoices.filter((invoice) =>
      linkedInvoiceIds.has(String(invoice?.id))
    );
    if (linkedInvoices.length) {
      return pickLatestInvoice(linkedInvoices);
    }

    const latestId = [...linkedInvoiceIds].sort(
      (a, b) => (Number(b) || 0) - (Number(a) || 0)
    )[0];
    return latestId
      ? {
          id: latestId,
          invoiceType: "MEDICINE",
          patientId,
          patientName: patientInfo?.name,
        }
      : null;
  };

  const issuedInvoiceByDate = useMemo(() => {
    const map = {};
    Object.entries(groupedPrescriptions).forEach(([dateKey, dayPrescriptions]) => {
      map[dateKey] = findIssuedMedicineInvoiceForDate(dateKey, dayPrescriptions);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedPrescriptions, medicineInvoices, medicineTransactions, patientId, patientInfo]);

  const handlePrintMedicineInvoice = async (invoice) => {
    if (!invoice?.id || printingInvoiceId != null) return;
    setPrintingInvoiceId(invoice.id);
    try {
      let fullInvoice = invoice;
      // Prefer a fresh full invoice record so amounts/medicines reprint correctly.
      try {
        const fetched = await getInvoiceById(invoice.id);
        const record = Array.isArray(fetched) ? fetched[0] : fetched;
        if (record) fullInvoice = { ...invoice, ...record };
      } catch (error) {
        console.warn("Could not refresh medicine invoice before print:", error);
      }

      const fullPatient =
        patientInfo?.id != null
          ? patientInfo
          : await getPatientById(patientId).catch(() => patientInfo);
      const patientsLookup =
        fullPatient?.id != null
          ? {
              [fullPatient.id]: fullPatient,
              [String(fullPatient.id)]: fullPatient,
            }
          : patientId != null
            ? { [String(patientId)]: fullPatient || { id: patientId } }
            : {};

      await printInvoiceFromTable(
        {
          ...fullInvoice,
          invoiceType: fullInvoice.invoiceType || "MEDICINE",
          patientId:
            fullInvoice.patientId ??
            fullInvoice.patient_id ??
            fullPatient?.id ??
            patientId,
          patientName:
            fullInvoice.patientName ||
            fullInvoice.toName ||
            fullPatient?.name ||
            "",
        },
        {
          user,
          patientsLookup,
        }
      );
    } catch (error) {
      console.error("Failed to print medicine invoice:", error);
      toast.error(error?.message || "Failed to print medicine invoice");
    } finally {
      setPrintingInvoiceId(null);
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilterDoctor("");
    setFromDate("");
    setToDate("");
  };

  const handleExport = async () => {
    const rows = filteredList || [];
    const headers = ["Prescription Id", "Doctor Name", "Created At", "Medicines"];
    const tableRows = rows.map((p) => [
      p.id ?? p.prescriptionId ?? "",
      p.doctorName ?? "",
      p.createdAt ?? "",
      Array.isArray(p.medicines) ? p.medicines.map((m) => m.name).join("|") : "",
    ]);

    await downloadStyledTableExcel({
      filename: `prescriptions_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Prescriptions",
      title: "Prescriptions",
      tableHeaders: headers,
      tableRows,
    });
  };

  return (
    <Box sx={{ p: 4, backgroundColor: "#fff", minHeight: "100vh" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Header
          title={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={() => onBack ? onBack() : navigate(-1)}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  mr: 1,
                  p: 0.5,
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                {
                  patientInfo.name
                    ?.split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(" ")
                } Timeline
              </Typography>
            </Box>
          }
        />
      </Box>
      
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
        <Tabs 
        variant="scrollable"
                    scrollButtons="auto"
                    indicatorColor="primary"
                    sx={{ mb: 2 }} 
                    value={activeTab} 
                    onChange={handleTabChange}>
          {tabLabels.map((label, index) => (
            <Tab label={label} key={index} />
          ))}
        </Tabs>
        <Divider sx={{ my: 2 }} />

        <Box sx={TABLE_SEARCH_ROW_SX}>
          <TextField
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={TABLE_ICON_SX} fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
            <IconButton
              size="small"
              sx={TABLE_ICON_BUTTON_SX}
              onClick={() => setShowFilters((v) => !v)}
            >
              <FilterAltIcon
                fontSize="small"
                color={showFilters ? "primary" : "inherit"}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to Excel">
            <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              sx={TABLE_ICON_BUTTON_SX}
              onClick={async () => {
                await Promise.all([fetchPrescriptions(), fetchIssuedMedicineData()]);
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {showFilters && (
        <Stack spacing={1.5} direction={{ xs:'column', sm:'row' }} alignItems={{ xs:'stretch', sm:'center' }} sx={{ mb: 2 }}>
          <TextField label="Doctor name" value={filterDoctor} onChange={(e)=>setFilterDoctor(e.target.value)} />
          <TextField label="From date" type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} InputLabelProps={{shrink:true}} />
          <TextField label="To date" type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} InputLabelProps={{shrink:true}} />
          <Tooltip title="Clear filters"><IconButton onClick={clearFilters}><ClearAllIcon/></IconButton></Tooltip>
        </Stack>
        )}
        
        {activeTab === 0 && (
          <Box sx={{ position: 'relative' }}>
            {Object.keys(groupedPrescriptions).length === 0 ? (
              <Typography>No prescriptions available</Typography>
            ) : (
              Object.entries(groupedPrescriptions).map(([date, prescriptions]) => {
                const issuedInvoice = issuedInvoiceByDate[date];
                const showPrintInvoice =
                  isPharmacyAdmin &&
                  Array.isArray(prescriptions) &&
                  prescriptions.length > 0 &&
                  issuedInvoice?.id != null;

                return (
                <Box key={date} sx={{ mb: 3, borderRadius: 2, p: 2 }}>
                  {/* Vertical Line */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: "-10px", // Adjust this to position the line correctly
                      top: '0',
                      height: '100%',
                      width: '2px',
                      backgroundColor: '#1976d2',
                    }}
                  />
                  
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0.5,
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold" sx={{ color: "#1976d2" }}>
                        {date}
                      </Typography>
                      {showPrintInvoice && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={
                            printingInvoiceId != null &&
                            String(printingInvoiceId) === String(issuedInvoice.id) ? (
                              <CircularProgress size={14} />
                            ) : (
                              <PrintIcon fontSize="small" />
                            )
                          }
                          disabled={printingInvoiceId != null}
                          onClick={() => handlePrintMedicineInvoice(issuedInvoice)}
                        >
                          Print invoice
                        </Button>
                      )}
                    </Box>

                  {prescriptions.map((prescription, index) => (
                    <Box key={index} sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Prescription #{index + 1} - By {prescription.doctorName}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Date: {prescription.createdAt}
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleIssueMedicine(prescription)}
                          sx={{ mb: 1 }}
                        >
                          Issue Medicine
                        </Button>
                      </Box>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#3498db' }}>
                            <TableCell sx={{ color: "#fff" }}>Medicine Name</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Dosage</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Frequency</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Duration</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Notes</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {prescription.medicines.map((medicine, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{medicine.name}</TableCell>
                              <TableCell>{medicine.dosage}</TableCell>
                              <TableCell>{medicine.frequency}</TableCell>
                              <TableCell>{medicine.duration}</TableCell>
                              <TableCell>{medicine.notes}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  ))}
                </Box>
                );
              })
            )}
          </Box>
        )}
        
        {/* {activeTab === 1 && (
          <Box>
            <Typography variant="h6">Vital Signs</Typography>
            
          </Box>
        )} */}

      </Paper>

      {/* Issue Medicine Modal */}
      <IssueMedicineModal
        open={openModal}
        onClose={handleModalClose}
        medicines={selectedMedicines} // Pass the array of selected medicines
        onSubmit={handleMedicineSubmit}
        patientId={patientInfo.id} // Pass the patient id
        doctorName={selectedPrescription?.doctorName || null} // ✅ Pass doctor name from prescription
        doctorId={
          selectedPrescription?.doctorId ||
          selectedPrescription?.doctor_id ||
          null
        }
        appointmentId={
          selectedPrescription?.appointmentId ||
          selectedPrescription?.appointment_id ||
          null
        }
        prescriptionId={
          selectedPrescription?.id ||
          selectedPrescription?.prescriptionId ||
          selectedPrescription?.prescription_id ||
          null
        }
        departmentId={
          selectedPrescription?.departmentId ||
          selectedPrescription?.department_id ||
          null
        }
      />
      
    </Box>
  );
};

export default Prescriptions;
